/**
 * THE SPELL HINT UNDER A COPY FIELD (2026-08-22).
 *
 * Rides the same slot as `<Grc>` (the character counter) so a copy field has one
 * status line, not two competing ones — and like Grc it renders NOTHING when there
 * is nothing to say. A checker that occupies space while clean trains people to
 * ignore the space.
 *
 * FOUR DELIBERATE CHOICES.
 *
 * · IT NEVER BLOCKS. This is advice, not a gate. Nothing here feeds the Done gate:
 *   a name the dictionary has not met is not a reason a division cannot send, and
 *   the moment a spell check can block a send, someone disables it.
 *
 * · THE DICTIONARY LOADS ON MOUNT, NOT ON APP BOOT. The first copy field to render
 *   triggers the 540KB fetch; `loadSpell` is single-flight, so every other field
 *   mounting at the same time joins that one request.
 *
 * · TYPING IS DEBOUNCED, SUGGESTING IS NOT. `spellCheckText` over a field is
 *   sub-millisecond, but re-rendering suggestion chips on every keystroke makes the
 *   field feel like it is fighting you, so the check settles 400ms after the last
 *   keystroke. Suggestions are computed inside the check, so opening a word's
 *   chips costs nothing.
 *
 * · A FIX IS A SPLICE AT AN OFFSET, NOT A STRING REPLACE. `applySuggestion` uses
 *   the issue's start/end, so fixing the second `amenties` in a paragraph does not
 *   silently fix the first and leave the caret somewhere unexpected.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  loadSpell,
  spellCheckText,
  spellSubscribe,
  spellReady,
  spellLearn,
  applySuggestion,
  type SpellIssue,
} from "../services/spell";

/** shared by the field hint and the whole-email panel */
export function useSpellIssues(value: string, delay = 400): SpellIssue[] {
  const [issues, setIssues] = useState<SpellIssue[]>([]);
  const vRef = useRef(value);
  vRef.current = value;

  const run = useCallback(() => {
    const next = spellReady() ? spellCheckText(vRef.current) : [];
    /* NOTHING-TO-NOTHING IS NOT A STATE CHANGE. Clean copy is the common case and
       the dictionary landing would otherwise re-render every mounted copy field to
       tell each one it is still clean. Skipping that also silences React's
       act() warning in tests, which was real noise and not a real update. */
    setIssues((prev) => (prev.length === 0 && next.length === 0 ? prev : next));
  }, []);

  /* one loader for the whole app; re-check when it lands or when the word lists move */
  useEffect(() => {
    let live = true;
    loadSpell().then(() => {
      if (live) run();
    });
    const off = spellSubscribe(() => {
      if (live) run();
    });
    return () => {
      live = false;
      off();
    };
  }, [run]);

  useEffect(() => {
    if (!spellReady()) return;
    const t = window.setTimeout(run, delay);
    return () => window.clearTimeout(t);
  }, [value, delay, run]);

  return issues;
}

export function SpellHint({
  value,
  onFix,
}: {
  value: string;
  /** omit to get a read-only hint — the panel passes one, a locked field does not */
  onFix?: (next: string) => void;
}) {
  const issues = useSpellIssues(value);
  const [open, setOpen] = useState<number | null>(null);

  /* the open word is identified by offset, which moves the moment the text changes;
     collapsing on any edit is simpler and never leaves a stale chip row */
  useEffect(() => setOpen(null), [value]);

  if (!issues.length) return null;

  const iss = open != null ? issues.find((i) => i.start === open) : null;

  return (
    <div className="splh" data-count={issues.length}>
      <div className="splh-row">
        <span className="splh-ic" aria-hidden="true">
          ABC
        </span>
        <span className="splh-lab">
          {issues.length === 1 ? "1 possible spelling" : issues.length + " possible spellings"}
        </span>
        {issues.map((i) => (
          <button
            key={i.start}
            type="button"
            className={"splh-w" + (open === i.start ? " on" : "")}
            title={i.suggestions.length ? "Suggestions: " + i.suggestions.join(", ") : "No suggestion"}
            onClick={() => setOpen(open === i.start ? null : i.start)}
          >
            {i.word}
          </button>
        ))}
      </div>

      {iss && (
        <div className="splh-sug">
          {iss.suggestions.length ? (
            iss.suggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="splh-fix"
                disabled={!onFix}
                onClick={() => {
                  onFix?.(applySuggestion(value, iss, s));
                  setOpen(null);
                }}
              >
                {s}
              </button>
            ))
          ) : (
            <span className="splh-none">No suggestion</span>
          )}
          <button
            type="button"
            className="splh-add"
            title="Stop flagging this word — remembered on this browser"
            onClick={() => {
              spellLearn(iss.word);
              setOpen(null);
            }}
          >
            Add to dictionary
          </button>
        </div>
      )}
    </div>
  );
}

export default SpellHint;
